import styled from "styled-components";
import SubTotals from "../admin/Orders/OrderLineItem/SubTotals";
import { Button, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import BasketItem from "./basketItem";
import { TableActionButtonDelete } from "../../@ui/TableActionButtonDelete";
import { useNavigate } from "react-router-dom";
import { useBasketContext } from "../../context/Basket/BasketContext";
import { useTenantContext } from "../../context/Tenant/TenantContext";
import useBasketService from "../../hooks/useBasketService";
import { useEffect, useState } from "react";
import { BasketOverlapWarning } from "../../types/Basket/BasketOverlapWarning";
import { ConfirmOverlapCheckoutModal } from "../modal/Basket/ConfirmOverlapCheckoutModal";

const Container = styled.div`
  height: 100vh;
`;

const Content = styled.div`
  height: calc(100vh - 67.25px);
  padding: 2rem 10rem;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 5px;
`;

const ItemsContainer = styled.div`
  & > *:last-child {
    border-bottom: 1px solid #ccc;
  }
`;

const Basket = () => {
  const { basket, setBasket } = useBasketContext();
  const { config } = useTenantContext();
  const basketService = useBasketService();
  const navigate = useNavigate();
  const [acceptedEULA, setAcceptedEULA] = useState(false);
  const [overMaxCheckout, setOverMaxCheckout] = useState(false);
  const [overlapWarnings, setOverlapWarnings] = useState<
    BasketOverlapWarning[]
  >([]);
  const [checkingOverlaps, setCheckingOverlaps] = useState(false);
  const [hoveredWarningIndex, setHoveredWarningIndex] = useState<
    number | null
  >(null);
  const [showOverlapConfirm, setShowOverlapConfirm] = useState(false);

  useEffect(() => {
    setOverMaxCheckout((basket?.totalIncTax ?? 0) >= 1000000);
  }, [basket]);

  useEffect(() => {
    if (!basket || basket.lineItems.length === 0) {
      setOverlapWarnings([]);
      return;
    }
    setCheckingOverlaps(true);
    basketService
      .GetOverlapWarnings()
      .then(setOverlapWarnings)
      .finally(() => setCheckingOverlaps(false));
  }, [basket, basketService]);

  const overlappingItemIds = new Set(
    overlapWarnings.flatMap((w) => [w.orderItemIdA, w.orderItemIdB]),
  );

  const hoveredItemIds =
    hoveredWarningIndex !== null && overlapWarnings[hoveredWarningIndex]
      ? new Set([
          overlapWarnings[hoveredWarningIndex].orderItemIdA,
          overlapWarnings[hoveredWarningIndex].orderItemIdB,
        ])
      : new Set<string>();

  const removeItem = async (id: string) => {
    try {
      const newBasket = await basketService.RemoveFromBasket(id);
      setBasket(newBasket);
      if (newBasket == null || newBasket.lineItems.length === 0) {
        navigate("/");
      }
    } catch (e) {
      navigate("/");
    }
  };

  const emptyBasket = async () => {
    try {
      const newBasket = await basketService.EmptyBasket();
      setBasket(newBasket);
    } finally {
      navigate("/");
    }
  };

  const checkout = async () => {
    const url = await basketService.ConfirmOrder();
    window.location.href = url;
  };

  const openEULA = async () => {
    const url = await basketService.GetEULA();
    if (url) window.open(url, "_blank");
  };

  const name = config.name === "Default" ? "Datacutter" : config.name;

  return (
    <Container className="card-theme text-white">
      <Content className="d-flex flex-row w-100">
        <div
          style={{
            width: "80%",
          }}
          className="p-2"
        >
          <Card className="p-3 h-100 overflow-auto bg-light">
            <div className="d-flex flex-row justify-content-between align-items-end mb-2 text-black">
              <h4 className="mb-0 text-black">Basket</h4>
              <p className="mb-0 text-black">Price</p>
            </div>
            {checkingOverlaps && (
              <div className="mt-2 mb-0 d-flex align-items-center gap-2 text-black">
                <Spinner animation="border" size="sm" />
                <span>Checking for overlapping items...</span>
              </div>
            )}
            {!checkingOverlaps && overlapWarnings.length > 0 && (
              <div className="mt-2 mb-0 text-danger">
                {overlapWarnings.map((warning, i) => (
                  <OverlayTrigger
                    key={i}
                    placement="right"
                    overlay={
                      <Tooltip id={`overlap-warning-tooltip-${i}`}>
                        See below for related highlighted items.
                      </Tooltip>
                    }
                  >
                    <p
                      className="mb-1"
                      style={{ cursor: "default", width: "fit-content" }}
                      onMouseEnter={() => setHoveredWarningIndex(i)}
                      onMouseLeave={() => setHoveredWarningIndex(null)}
                    >
                      Warning: items for {warning.layerDisplayName} overlap by{" "}
                      {warning.overlapPercentage.toFixed(0)}%.{" "}
                      <FontAwesomeIcon icon={faCircleInfo} />
                    </p>
                  </OverlayTrigger>
                ))}
                <p className="text-danger"><b>This may result in paying for duplicate data.</b></p>
              </div>
            )}
            {basket && (
              <ItemsContainer>
                {basket.lineItems.map((item) => (
                  <BasketItem
                    key={item.id}
                    removeItem={removeItem}
                    item={item}
                    hasOverlapWarning={overlappingItemIds.has(item.id)}
                    isHoverHighlighted={hoveredItemIds.has(item.id)}
                  />
                ))}
              </ItemsContainer>
            )}
            <TableActionButtonDelete
              onClick={() => emptyBasket()}
              className="mt-2"
            >
              Empty Basket
            </TableActionButtonDelete>
            {basket && basket.lineItems.length > 0 && (
              <>
              <div className="mt-2 mb-0 text-danger">
                {basket.lineItems.some((item) => item.layerName === "Inrix:Trip") && (
                    <span>Once payment is complete, your data will be prepared and delivered within a few hours.</span>
                  )}
              </div>
              <div className="mt-1 text-black">
                <label>
                  <input
                    type="checkbox"
                    checked={acceptedEULA}
                    onChange={(e) => setAcceptedEULA(e.target.checked)}
                  />{" "}
                  I have read and accept the{" "}
                  <Button
                    variant="link"
                    onClick={() => openEULA()}
                    className="px-0"
                  >
                    {name} Data End User Licence Agreement
                  </Button> 
                </label>
              </div>
              </>
            )}
          </Card>
        </div>
        <div style={{ width: "20%" }} className="p-2 h-100">
          <Card className="px-2 py-2 d-flex flex-column gap-2 bg-light text-black">
            {" "}
            {basket && (
              <SubTotals
                transactionCurrencyCode={basket!.transactionCurrencyCode}
                totalExTax={basket!.totalExTax}
                totalTax={basket!.totalTax}
                totalIncTax={basket!.totalIncTax}
              />
            )}
            {(() => {
              const checkoutDisabledReason = !acceptedEULA
                ? "You must accept the Data End User Licence Agreement before checking out."
                : overMaxCheckout
                  ? "Basket total exceeds the maximum allowed checkout amount."
                  : checkingOverlaps
                    ? "Checking for overlapping items..."
                    : null;

              const checkoutButton = (
                <Button
                  className="w-100"
                  onClick={() => {
                    if (overlapWarnings.length > 0) {
                      setShowOverlapConfirm(true);
                    } else {
                      checkout();
                    }
                  }}
                  disabled={checkoutDisabledReason !== null}
                >
                  Checkout
                </Button>
              );

              return checkoutDisabledReason ? (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="checkout-disabled-tooltip">
                      {checkoutDisabledReason}
                    </Tooltip>
                  }
                >
                  <span className="d-inline-block w-100">
                    {checkoutButton}
                  </span>
                </OverlayTrigger>
              ) : (
                checkoutButton
              );
            })()}
            {overMaxCheckout && (
              <p className="text-danger small mb-0">
                Basket total exceeds maximum allowed checkout amount of
                1,000,000.
                <br />
                <br />
                Please contact{" "}
                <a href={`mailto:${config.salesEmail}`}>{config.salesEmail}</a>.
              </p>
            )}
          </Card>
        </div>
      </Content>
      <ConfirmOverlapCheckoutModal
        show={showOverlapConfirm}
        warnings={overlapWarnings}
        isProcessing={false}
        onClose={() => setShowOverlapConfirm(false)}
        onConfirmed={() => {
          setShowOverlapConfirm(false);
          checkout();
        }}
      />
    </Container>
  );
};

export default Basket;
